import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Send, Mic, MicOff, ChevronRight, Sparkles, RotateCcw,
  Sun, Pill, Users, BookOpen, AlertTriangle, Settings,
  Hand, Home, ClipboardList, BookMarked, User, AlertCircle,
  FileText, Brain, Shield, Flame, Droplets, Lock,
  Hospital, Search, Key, Phone, Wifi, Volume2, VolumeX,
  MessageCircle, Zap
} from 'lucide-react';
import { AdelaarMascot } from './Branding';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Custom Markdown components for beautiful styling
const MarkdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-lg font-bold text-slate-900 mb-3 mt-4 first:mt-0 border-b border-slate-200 pb-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-bold text-slate-800 mb-2 mt-4 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-700 mb-2 mt-3 first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-sm text-slate-700 mb-3 last:mb-0 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc ml-5 mb-3 space-y-1.5 text-sm text-slate-700">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal ml-5 mb-3 space-y-1.5 text-sm text-slate-700">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed pl-1">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic">{children}</em>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    if (isInline) {
      return <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>;
    }
    return <code className={className}>{children}</code>;
  },
  pre: ({ children }) => (
    <pre className="bg-slate-800 text-slate-100 p-3 rounded-lg text-xs overflow-x-auto mb-3 font-mono">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-purple-400 pl-4 py-2 my-3 bg-purple-50 rounded-r-lg italic text-slate-600">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-slate-200 shadow-sm">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gradient-to-r from-slate-100 to-slate-50">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-slate-50 transition-colors">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide border-b border-slate-200">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-slate-700 align-top">{children}</td>
  ),
  hr: () => <hr className="my-4 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-purple-600 hover:text-purple-800 underline font-medium" target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

// UI Categories for the sidebar with professional Lucide icons
const UI_CATEGORIES = [
  { 
    id: 'inwerken', 
    name: 'Eerste Dienst', 
    icon: Sparkles,
    color: '#10b981',
    gradient: 'from-emerald-400 to-teal-500',
    subcategories: [
      { id: 'start_dienst', name: 'Start van de dienst', icon: Sun },
      { id: 'kennismaken_clienten', name: 'Kennismaken met cliënten', icon: Hand },
      { id: 'rondleiding', name: 'Rondleiding locatie', icon: Home },
    ]
  },
  { 
    id: 'medicatie', 
    name: 'Medicatie', 
    icon: Pill,
    color: '#8b5cf6',
    gradient: 'from-violet-400 to-purple-500',
    subcategories: [
      { id: 'medicatie_algemeen', name: 'Medicatie algemeen', icon: BookMarked },
      { id: 'medicatie_clienten', name: 'Per cliënt', icon: User },
      { id: 'medicatie_bijwerking', name: 'Bijwerkingen', icon: AlertCircle },
      { id: 'medicatie_weigering', name: 'Medicatie weigering', icon: X },
    ]
  },
  { 
    id: 'clienten', 
    name: 'Cliënten', 
    icon: Users,
    color: '#ec4899',
    gradient: 'from-pink-400 to-rose-500',
    subcategories: [
      { id: 'client_overzicht', name: 'Cliëntoverzicht', icon: ClipboardList },
      { id: 'client_zorgplan', name: 'Zorgplannen', icon: FileText },
      { id: 'client_gedrag', name: 'Gedragsprotocollen', icon: Brain },
    ]
  },
  { 
    id: 'protocollen', 
    name: 'Protocollen', 
    icon: BookOpen,
    color: '#f59e0b',
    gradient: 'from-amber-400 to-orange-500',
    subcategories: [
      { id: 'protocol_agressie', name: 'Agressieprotocol', icon: Shield },
      { id: 'protocol_brand', name: 'Brandprotocol', icon: Flame },
      { id: 'protocol_hygiene', name: 'Hygiëne', icon: Droplets },
      { id: 'protocol_privacy', name: 'Privacy & AVG', icon: Lock },
    ]
  },
  { 
    id: 'noodgevallen', 
    name: 'Noodgevallen', 
    icon: AlertTriangle,
    color: '#ef4444',
    gradient: 'from-red-400 to-rose-500',
    subcategories: [
      { id: 'nood_medisch', name: 'Medisch noodgeval', icon: Hospital },
      { id: 'nood_agressie', name: 'Acute agressie', icon: AlertCircle },
      { id: 'nood_vermissing', name: 'Vermissing cliënt', icon: Search },
      { id: 'nood_brand', name: 'Brand', icon: Flame },
    ]
  },
  { 
    id: 'praktisch', 
    name: 'Praktisch', 
    icon: Settings,
    color: '#6366f1',
    gradient: 'from-indigo-400 to-blue-500',
    subcategories: [
      { id: 'praktisch_sleutels', name: 'Sleutels & toegang', icon: Key },
      { id: 'praktisch_contact', name: 'Contactgegevens', icon: Phone },
      { id: 'praktisch_wifi', name: 'WiFi & systemen', icon: Wifi },
    ]
  },
];

// Suggested quick questions
const QUICK_SUGGESTIONS = [
  { q: 'Wat moet ik doen bij mijn eerste dienst?', icon: '🌅' },
  { q: 'Hoe geef ik medicatie volgens de 5 J\'s?', icon: '💊' },
  { q: 'Wat doe ik bij een noodgeval?', icon: '🚨' },
];

// Category-based suggestions
const CATEGORY_SUGGESTIONS = {
  inwerken: [
    { q: 'Wat moet ik doen aan het begin van mijn eerste dienst?', icon: '🌅' },
    { q: 'Hoe maak ik kennis met de cliënten?', icon: '👋' },
    { q: 'Waar vind ik de overdracht?', icon: '📋' },
  ],
  medicatie: [
    { q: 'Hoe geef ik medicatie volgens de 5 J\'s?', icon: '💊' },
    { q: 'Wat doe ik als een cliënt medicatie weigert?', icon: '🚫' },
    { q: 'Waar vind ik de medicatielijsten?', icon: '📋' },
  ],
  clienten: [
    { q: 'Geef me een overzicht van alle cliënten', icon: '👥' },
    { q: 'Waar vind ik de zorgplannen?', icon: '📄' },
    { q: 'Wat zijn de gedragsprotocollen?', icon: '🧠' },
  ],
  protocollen: [
    { q: 'Wat moet ik doen bij agressie?', icon: '🛡️' },
    { q: 'Wat is het brandprotocol?', icon: '🔥' },
    { q: 'Wat zijn de hygiëneregels?', icon: '🧼' },
  ],
  noodgevallen: [
    { q: 'Wat doe ik bij een medisch noodgeval?', icon: '🏥' },
    { q: 'Hoe handel ik bij acute agressie?', icon: '⚠️' },
    { q: 'Wat als een cliënt vermist is?', icon: '🔍' },
  ],
  praktisch: [
    { q: 'Waar vind ik de sleutels?', icon: '🔑' },
    { q: 'Wat zijn de belangrijke telefoonnummers?', icon: '📞' },
    { q: 'Wat is het WiFi wachtwoord?', icon: '📶' },
  ],
};

export const AIChatModal = ({
  isOpen,
  onClose,
  messages,
  setMessages,
  input,
  setInput,
  loading,
  sendMessage,
  isRecording,
  startRecording,
  stopRecording,
  selectedLocation,
  ttsEnabled = true,
  setTtsEnabled,
  ttsPlaying = false,
  stopTTS,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleCategoryClick = (categoryId, subcategoryId = null) => {
    if (subcategoryId) {
      const category = UI_CATEGORIES.find(c => c.id === categoryId);
      const sub = category?.subcategories?.find(s => s.id === subcategoryId);
      if (sub) {
        sendMessage(`Vertel me over: ${sub.name}`);
        setSelectedCategory(categoryId);
        setExpandedCategory(null);
      }
    } else {
      setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
      setSelectedCategory(categoryId);
    }
  };

  const currentSuggestions = CATEGORY_SUGGESTIONS[selectedCategory] || QUICK_SUGGESTIONS;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Backdrop with blur */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-50"
        onClick={onClose}
        data-testid="chat-backdrop"
      />
      
      {/* Modal Container - iPhone Style */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', duration: 0.5, bounce: 0.2 }}
        className="fixed inset-2 sm:inset-4 md:inset-6 lg:inset-10 z-50 flex flex-col overflow-hidden bg-[#F8FAFC] rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl ring-1 ring-black/5"
        data-testid="ai-chat-modal"
      >
        {/* Glass Header */}
        <div className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Left: Logo & Title */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <AdelaarMascot className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Sahbi AI
                </h2>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">Online</span>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* TTS Toggle */}
              {setTtsEnabled && (
                <button 
                  onClick={() => {
                    if (ttsPlaying && stopTTS) stopTTS();
                    setTtsEnabled(!ttsEnabled);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all text-sm font-medium ${
                    ttsEnabled 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  } ${ttsPlaying ? 'animate-pulse' : ''}`}
                  title={ttsEnabled ? 'Spraak uitschakelen' : 'Spraak inschakelen'}
                  data-testid="tts-toggle-btn"
                  aria-label={ttsEnabled ? 'Voorlezen aan' : 'Voorlezen uit'}
                >
                  {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  <span className="hidden sm:inline">{ttsEnabled ? 'Aan' : 'Uit'}</span>
                </button>
              )}

              {/* New Chat */}
              {messages.length > 0 && (
                <button 
                  onClick={() => {
                    if (stopTTS) stopTTS();
                    setMessages([]);
                    setSelectedCategory(null);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600 text-sm font-medium"
                  title="Nieuw gesprek"
                  data-testid="new-chat-btn"
                  aria-label="Nieuw gesprek starten"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Nieuw</span>
                </button>
              )}

              {/* Close */}
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="close-chat-btn"
                aria-label="Sluit chat"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Mobile Category Chips - Horizontal Scroll */}
          <div className="lg:hidden mt-3 -mx-4 sm:-mx-6 px-4 sm:px-6">
            <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
              {UI_CATEGORIES.map((cat) => {
                const IconComponent = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(isActive ? null : cat.id);
                      setExpandedCategory(null);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full whitespace-nowrap transition-all text-sm font-medium ${
                      isActive 
                        ? `bg-gradient-to-r ${cat.gradient} text-white shadow-md` 
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`category-chip-${cat.id}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Desktop Sidebar */}
          <div className="hidden lg:flex w-72 flex-col border-r border-slate-200/50 bg-white/50 backdrop-blur-md">
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
                Categorieën
              </h3>
              <div className="space-y-1">
                {UI_CATEGORIES.map((cat) => {
                  const IconComponent = cat.icon;
                  const isExpanded = expandedCategory === cat.id;
                  const isActive = selectedCategory === cat.id;
                  return (
                    <div key={cat.id}>
                      <button
                        onClick={() => handleCategoryClick(cat.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                          isActive 
                            ? 'bg-white shadow-sm ring-1 ring-slate-200' 
                            : 'hover:bg-white/80'
                        }`}
                        data-testid={`category-btn-${cat.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                              isActive ? `bg-gradient-to-br ${cat.gradient} shadow-md` : ''
                            }`}
                            style={!isActive ? { backgroundColor: `${cat.color}15` } : {}}
                          >
                            <IconComponent 
                              className="w-4 h-4" 
                              style={{ color: isActive ? 'white' : cat.color }} 
                            />
                          </div>
                          <span className={`text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                            {cat.name}
                          </span>
                        </div>
                        <ChevronRight 
                          className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                        />
                      </button>
                      
                      {/* Subcategories */}
                      <AnimatePresence>
                        {isExpanded && cat.subcategories && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-4 mt-1 space-y-0.5 overflow-hidden"
                          >
                            {cat.subcategories.map((sub) => {
                              const SubIcon = sub.icon;
                              return (
                                <button
                                  key={sub.id}
                                  onClick={() => handleCategoryClick(cat.id, sub.id)}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm text-slate-500 hover:bg-white hover:text-slate-700 transition-all"
                                  data-testid={`subcategory-btn-${sub.id}`}
                                >
                                  <SubIcon className="w-4 h-4" />
                                  <span>{sub.name}</span>
                                </button>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {messages.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center h-full text-center px-4"
                >
                  {/* Welcome Illustration */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl shadow-purple-500/30">
                      <MessageCircle className="w-12 h-12 text-white" />
                    </div>
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg"
                    >
                      <Sparkles className="w-4 h-4 text-white" />
                    </motion.div>
                  </div>
                  
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">
                    Hallo! Ik ben Sahbi
                  </h3>
                  <p className="text-slate-500 mb-8 max-w-sm">
                    Jouw AI-assistent voor alle vragen over de zorg. Stel een vraag of kies een onderwerp.
                  </p>
                  
                  {/* Quick Suggestions */}
                  <div className="w-full max-w-md space-y-2">
                    {currentSuggestions.map((item, idx) => (
                      <motion.button
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        onClick={() => sendMessage(item.q)}
                        className="w-full p-4 text-left bg-white hover:bg-slate-50 rounded-2xl border border-slate-200 hover:border-slate-300 transition-all flex items-center gap-4 group shadow-sm hover:shadow"
                        data-testid={`suggestion-${idx}`}
                      >
                        <span className="text-2xl">{item.icon}</span>
                        <span className="text-sm text-slate-700 flex-1">{item.q}</span>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] sm:max-w-[75%] ${
                        msg.role === 'user' 
                          ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-lg shadow-purple-500/20' 
                          : 'bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-4 py-4 shadow-sm'
                      }`}>
                        {msg.role === 'assistant' && (
                          <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-sm">
                                <AdelaarMascot className="w-4 h-4" />
                              </div>
                              <span className="text-xs font-semibold text-slate-500">Sahbi AI</span>
                            </div>
                            {/* Play TTS button */}
                            {setTtsEnabled && (
                              <button
                                onClick={() => {
                                  if (stopTTS) stopTTS();
                                  setTimeout(() => {
                                    if (typeof window !== 'undefined' && msg.content) {
                                      window.dispatchEvent(new CustomEvent('playTTS', { detail: msg.content }));
                                    }
                                  }, 100);
                                }}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  ttsPlaying 
                                    ? 'bg-purple-100 text-purple-600 animate-pulse' 
                                    : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                                }`}
                                title="Voorlezen"
                                data-testid={`play-tts-msg-${idx}`}
                                aria-label="Voorlezen"
                              >
                                <Volume2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                        {msg.role === 'user' ? (
                          <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                        ) : (
                          <div className="ai-response">
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                              components={MarkdownComponents}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Loading indicator */}
                  {loading && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                        <div className="flex items-center gap-1.5">
                          <motion.div 
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                          />
                          <motion.div 
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0.15 }}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                          />
                          <motion.div 
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0.3 }}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area - iMessage Style */}
            <div className="p-4 bg-white/80 backdrop-blur-xl border-t border-slate-200/50">
              {/* Recording indicator */}
              {isRecording && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-3xl mx-auto mb-3 flex items-center gap-2 text-red-600"
                >
                  <motion.div 
                    animate={{ scale: [1, 1.5, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="w-3 h-3 bg-red-500 rounded-full"
                  />
                  <span className="text-sm font-medium">Aan het luisteren...</span>
                </motion.div>
              )}
              
              <div className="max-w-3xl mx-auto flex items-end gap-2">
                {/* Mic Button */}
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={loading}
                  className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                    isRecording 
                      ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse' 
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700'
                  }`}
                  title={isRecording ? 'Stop opname' : 'Spraakbericht'}
                  data-testid="mic-btn"
                  aria-label={isRecording ? 'Stop opname' : 'Spraakbericht'}
                >
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>

                {/* Input Field */}
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input || ''}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && input?.trim() && sendMessage()}
                    placeholder="Stel je vraag..."
                    className="w-full px-5 py-3 bg-slate-100 border-0 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:bg-white transition-all placeholder:text-slate-400"
                    data-testid="chat-input"
                    aria-label="Chat invoerveld"
                  />
                </div>

                {/* Send Button */}
                <button
                  onClick={() => sendMessage()}
                  disabled={!input?.trim() || loading}
                  className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                    input?.trim() && !loading
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:scale-105'
                      : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                  }`}
                  data-testid="send-btn"
                  aria-label="Verstuur bericht"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIChatModal;
